/**
 * 앱 스토어 URL 단일 소스.
 *  설치 유도(배너 / 앱 전용 기능 안내)가 여러 곳에 생겨서 URL 을 한 곳으로 모았다.
 *  URL 이 비어 있으면 설치 유도 UI 는 자동 휴면(안내만 표시).
 */
import { OS, detectOS } from "util/userAgent";
import { getAid, track, flush } from "util/appAnalytics";
import { getLandingUtm } from "util/landingParams";

// 스토어 상품 페이지 주소. 설치 배너(AppInstallBanner)·앱 전용 안내(AppOnlyModalHost)·인증 대기 공고 상세의
//  설치 안내가 아래 공개 여부 플래그를 거쳐 이 값을 쓴다(앱 웹뷰 안에서는 각 화면이 숨긴다).
const APP_STORE_PAGE = "https://apps.apple.com/app/id6809101859";
const PLAY_STORE_PAGE = "https://play.google.com/store/apps/details?id=com.muyeon.app";

// ⚠️ 스토어 공개 여부 — 미공개 상태로 링크를 열면 "앱을 찾을 수 없음"이 떠서 광고 유입자가 이탈한다.
//  2026-10-21 App Store 공개 확인(상품 페이지 200). 2026-10-31 Play 공개 확인(상품 페이지 200).
export const STORE_RELEASED = { ios: true, android: true };

export const APP_STORE_URL = STORE_RELEASED.ios ? APP_STORE_PAGE : "";
export const PLAY_STORE_URL = STORE_RELEASED.android ? PLAY_STORE_PAGE : "";

/**
 * Play 스토어 링크에 설치 리퍼러를 싣는다 — 앱이 첫 실행 때 Install Referrer API 로 그대로 돌려받아
 *  "이 설치는 어느 웹 방문(aid)·어느 광고(UTM)에서 왔는지"를 확정한다(이용 행동 로그, 2026-10-19).
 *  App Store 는 이런 값을 넘기지 않아 서버가 같은 IP·OS·24시간으로 추정 연결한다.
 */
export function withInstallReferrer(url) {
  if (!url || !url.startsWith(PLAY_STORE_PAGE)) return url;
  const ref = new URLSearchParams();
  ref.set("aid", getAid());
  const utm = getLandingUtm();
  ["utm_source", "utm_medium", "utm_campaign", "utm_content"].forEach((k) => {
    if (typeof utm[k] === "string" && utm[k]) ref.set(k, utm[k].slice(0, 100));
  });
  return `${url}${url.includes("?") ? "&" : "?"}referrer=${encodeURIComponent(ref.toString())}`;
}

/** 스토어 이동 기록 — 앱 설치 퍼널의 '설치 버튼' 단계. 페이지를 떠나므로 바로 보낸다. */
export function trackStoreClick(url, placement) {
  const store = url && url.startsWith(PLAY_STORE_PAGE) ? "AOS" : "IOS";
  track("store_click", { target: `store:${placement || "unknown"}`, props: { store } });
  flush(true);
}

/** 현재 기기 OS 에 맞는 스토어 URL(없으면 null — 데스크톱/미출시). AOS 는 설치 리퍼러 포함. */
export function storeUrlForOS() {
  const os = detectOS();
  if (os === OS.IOS) return APP_STORE_URL || null;
  if (os === OS.ANDROID) return PLAY_STORE_URL ? withInstallReferrer(PLAY_STORE_URL) : null;
  return null;
}

/** 스토어로 이동(URL 없으면 아무것도 하지 않고 false). */
export function openStore(placement = "app_only") {
  const url = storeUrlForOS();
  if (!url) return false;
  trackStoreClick(url, placement);
  window.open(url, "_blank", "noopener");
  return true;
}
